import { html, render } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { choose } from 'lit/directives/choose.js';
import { msg } from '@omegagrid/localize';
import { customElement, property } from 'lit/decorators.js';
import { events } from '@omegagrid/core';
import { Grid, GridEditEvent } from '@omegagrid/grid';
import { BaseVisualizationEditor } from './baseEditor';
import { ColorCellEditor, colorCellRenderer } from './colorCellEditor';
import { ChartVisualization } from '../chart';
import type { ChartSeries, ChartViewOptions } from '@reporting/rp-types/analytics';

/**
 * Editor of the chart view options - chart/legend properties, series and
 * plot bands so far.
 *
 * The plot band form is shared with the gauge editor (see
 * BaseVisualizationEditor) and rendered per axis - bounds of the value axis
 * bands are values, bounds of the category axis bands are category indexes.
 * Both accept a number or a cell reference (=A1), so the plot bands live in
 * the data tab next to the report data grid.
 */
@customElement('rp-visual-chart-editor')
export class ChartVisualizationEditor extends BaseVisualizationEditor<ChartVisualization> {

	@property({type: String})
	activeTab: 'data'|'props'|'series'|'json' = 'data';

	get viewOptions() {
		return super.viewOptions as ChartViewOptions;
	}

	get legendOptions() {
		this.viewOptions.legend ??= {};
		return this.viewOptions.legend;
	}

	/**
	 * Chart the series form is built from. A chart view split by the chart axis
	 * renders several charts of the same shape, the first one names the series.
	 */
	private get chart(): ChartVisualization {
		return this.visualization?.getVisualizations()
			.find(visualization => visualization instanceof ChartVisualization) as ChartVisualization;
	}

	/**
	 * Build/rebuild the series grid - names from the chart, colors from the
	 * view options. The first data row is synthetic: the series defaults
	 * inherited by every series without an own value (e.g. a common color).
	 */
	private setSeriesGrid(grid?: Grid) {
		if (!grid) return;
		const names = this.chart?.seriesNames ?? [];
		const series = this.viewOptions.series ?? [];

		const colorCell = (color: string) => ({
			v: color,
			e: true,
			renderer: colorCellRenderer,
			editorFactory: () => new ColorCellEditor(),
		});

		/** dropdown cell handled by the built-in grid dropdown editor */
		const dropdownCell = (value: string, items: {k: string, v: string}[]) => ({
			v: value,
			dv: items.find(item => item.k === value)?.v,
			t: 's' as const,
			e: true,
			content: items,
		});

		const visibilityItems = [
			{k: '', v: msg('Auto')},
			{k: '1', v: msg('Visible')},
			{k: '0', v: msg('Hidden')},
		];

		const visibilityCell = (enabled: boolean|undefined) => dropdownCell(
			enabled == null ? '' : (enabled ? '1' : '0'),
			visibilityItems
		);

		const typeItems = [
			{k: '', v: msg('Auto')},
			{k: 'column', v: msg('visual.column')},
			{k: 'line', v: msg('visual.line')},
			{k: 'spline', v: msg('visual.spline')},
			{k: 'area', v: msg('visual.area')},
			{k: 'scatter', v: msg('visual.scatter')},
		];

		const typeCell = (serie: ChartSeries) => dropdownCell(serie?.type ?? '', typeItems);

		const shapeItems = [
			{k: '', v: msg('None')},
			{k: 'square', v: msg('Square')},
			{k: 'roundedRect', v: msg('Rounded')},
			{k: 'circle', v: msg('Circle')},
			{k: 'callout', v: msg('Callout')},
		];

		const shapeCell = (serie: ChartSeries) => dropdownCell(serie?.data_labels?.shape ?? '', shapeItems);

		const symbolItems = [
			{k: '', v: msg('Auto')},
			{k: 'circle', v: msg('Circle')},
			{k: 'square', v: msg('Square')},
			{k: 'diamond', v: msg('Diamond')},
			{k: 'triangle', v: msg('Triangle')},
			{k: 'triangle-down', v: msg('Triangle down')},
		];

		const symbolCell = (serie: ChartSeries) => dropdownCell(serie?.marker?.symbol ?? '', symbolItems);

		grid.setSourceData({
			columns: [
				{w: 'auto', data: 0, header: [null, {label: msg('Serie')}]},
				{w: 80, data: 1, header: [null, {label: msg('Type')}]},
				{w: 70, t: 't', data: 2, l: 0, header: [{label: msg('Color'), cs: 2}, {label: msg('Default')}]},
				{w: 70, t: 't', data: 3, l: 1, header: [null, {label: msg('Negative')}]},
				{w: 70, data: 4, l: 0, header: [{label: msg('Labels'), cs: 2}, {label: msg('Visible')}]},
				{w: 80, data: 5, l: 1, header: [null, {label: msg('Shape')}]},
				{w: 70, data: 6, l: 0, header: [{label: msg('Marker'), cs: 2}, {label: msg('Visible')}]},
				{w: 80, data: 7, l: 1, header: [null, {label: msg('Symbol')}]},
			],
			rows: [
				{
					data: [
						{v: msg('All series'), s: {italic: true}},
						typeCell(this.viewOptions.series_defaults),
						colorCell(this.viewOptions.series_defaults?.color),
						colorCell(this.viewOptions.series_defaults?.negative_color),
						visibilityCell(this.viewOptions.series_defaults?.data_labels?.enabled),
						shapeCell(this.viewOptions.series_defaults),
						visibilityCell(this.viewOptions.series_defaults?.marker?.enabled),
						symbolCell(this.viewOptions.series_defaults),
					]
				},
				...names.map((name, index) => ({
					data: [
						{v: name || `${msg('Series')} ${index + 1}`},
						typeCell(series[index]),
						colorCell(series[index]?.color),
						colorCell(series[index]?.negative_color),
						visibilityCell(series[index]?.data_labels?.enabled),
						shapeCell(series[index]),
						visibilityCell(series[index]?.marker?.enabled),
						symbolCell(series[index]),
					]
				})),
			],
			options: {
				rows: names.length + 1,
				columns: 8,
				rowNumbers: true,
				columnTree: false,
				columnHeaderTreeButtons: true,
				colLetters: false,
				autofill: false,
				undoRedo: false,
				commandWindow: false,
				rowResize: false,
				columnResize: false,
				emptyPlaceholder: false,
			}
		});
	}

	/** edits from the series grid go to the view options (row 0 = the defaults) */
	private _onSeriesEdit = (e: GridEditEvent) => {
		e.stopPropagation();
		if (e.editType !== 'value') return;
		e.cells?.forEach((cell, i) => {
			const target = cell.r === 0 ? 'series_defaults' : `series.${cell.r - 1}`;
			const value = e.newValues?.[i];
			const empty = value == null || value === '';

			switch (cell.c) {
				case 1:
					this.updateProperty(`${target}.type`, empty ? undefined : value.toString());
					break;
				case 2:
					this.updateProperty(`${target}.color`, empty ? undefined : value.toString().replace(/^#+/g, ''));
					break;
				case 3:
					this.updateProperty(`${target}.negative_color`, empty ? undefined : value.toString().replace(/^#+/g, ''));
					break;
				case 4:
					this.updateProperty(`${target}.data_labels.enabled`, empty ? undefined : value === '1');
					break;
				case 5:
					this.updateProperty(`${target}.data_labels.shape`, empty ? undefined : value.toString());
					break;
				case 6:
					this.updateProperty(`${target}.marker.enabled`, empty ? undefined : value === '1');
					break;
				case 7:
					this.updateProperty(`${target}.marker.symbol`, empty ? undefined : value.toString());
					break;
			}
		});
	}

	renderChartForm = () => html`
		<div class="row" style="display: flex; flex-direction: row; gap: 4px">
			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="inverted"
				data-type="boolean"
				@change="${this._onDataChange}"
				.caption="${msg('Inverted')}"
				.items="${[
					{k: '0', v: msg('No')},
					{k: '1', v: msg('Yes')},
				]}"
				.value="${this.viewOptions.inverted ? '1' : '0'}">
			</og-dropdown-list>

			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="split"
				data-type="boolean"
				@change="${this._onDataChange}"
				.caption="${msg('Split charts')}"
				.items="${[
					{k: '1', v: msg('Yes')},
					{k: '0', v: msg('No')},
				]}"
				.value="${this.viewOptions.split !== false ? '1' : '0'}">
			</og-dropdown-list>

			<og-dropdown-list
				class="field"
				color="transparent"
				data-key="zooming"
				data-type="enum"
				@change="${this._onDataChange}"
				.caption="${msg('Zooming')}"
				.items="${[
					{k: '', v: msg('None')},
					{k: 'x', v: 'X'},
					{k: 'y', v: 'Y'},
					{k: 'xy', v: 'XY'},
				]}"
				.value="${this.viewOptions.zooming ?? ''}">
			</og-dropdown-list>
		</div>
	`;

	renderAxisForm = (axis: 'x_axis'|'y_axis') => {
		const options = this.viewOptions[axis] ?? {};
		return html`
			<div class="row">
				<og-input
					class="field"
					data-key="${axis}.title"
					data-type="string"
					@change="${this._onDataChange}"
					.placeholder="${msg('Title')}"
					.value="${options.title ?? ''}">
				</og-input>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-input
					class="field"
					data-key="${axis}.min"
					data-type="string"
					@focus="${this._onFocus}"
					@change="${this._onDataChange}"
					.placeholder="${msg('Minimum')}"
					.value="${options.min ?? ''}">
				</og-input>

				<og-input
					class="field"
					data-key="${axis}.max"
					data-type="string"
					@focus="${this._onFocus}"
					@change="${this._onDataChange}"
					.placeholder="${msg('Maximum')}"
					.value="${options.max ?? ''}">
				</og-input>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="${axis}.labels"
					data-type="boolean"
					@change="${this._onDataChange}"
					.caption="${msg('Labels')}"
					.items="${[{k: '', v: msg('Auto')}, ...this.booleanItems()]}"
					.value="${options.labels == null ? '' : (options.labels ? '1' : '0')}">
				</og-dropdown-list>

				<og-input
					class="field"
					data-key="${axis}.tick_interval"
					data-type="number"
					@change="${this._onDataChange}"
					.placeholder="${msg('Tick interval')}"
					.value="${options.tick_interval ?? ''}">
				</og-input>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="${axis}.gridline_width"
					data-type="number"
					@change="${this._onDataChange}"
					.caption="${msg('Grid line')}"
					.items="${[
						{k: '', v: msg('Auto')},
						...new Array(6).fill(0).map((_, i) => ({k: i.toString(), v: i.toString()})),
					]}"
					.value="${(options.gridline_width ?? '').toString()}">
				</og-dropdown-list>

				<og-dropdown-colorpicker
					class="field"
					style="flex: 0 0 40px"
					color="transparent"
					data-key="${axis}.gridline_color"
					data-type="color"
					@change="${this._onDataChange}"
					.value="${options.gridline_color}">
				</og-dropdown-colorpicker>
			</div>
		`;
	};

	renderLegendForm = () => {
		const legend = this.legendOptions;
		return html`
			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="legend.enabled"
					data-type="boolean"
					@change="${this._onDataChange}"
					.caption="${msg('Legend')}"
					.items="${[{k: '', v: msg('Auto')}, ...this.booleanItems()]}"
					.value="${legend.enabled == null ? '' : (legend.enabled ? '1' : '0')}">
				</og-dropdown-list>

				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="legend.position"
					data-type="string"
					@change="${this._onDataChange}"
					.caption="${msg('Position')}"
					.items="${[
						{k: 'bottom', v: msg('Bottom')},
						{k: 'top', v: msg('Top')},
						{k: 'left', v: msg('Left')},
						{k: 'right', v: msg('Right')},
					]}"
					.value="${legend.position ?? 'bottom'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="legend.alignment"
					data-type="string"
					@change="${this._onDataChange}"
					.caption="${msg('Alignment')}"
					.items="${[
						{k: 'start', v: msg('Start')},
						{k: 'center', v: msg('Center')},
						{k: 'end', v: msg('End')},
					]}"
					.value="${legend.alignment ?? 'center'}">
				</og-dropdown-list>
			</div>
		`;
	};

	/**
	 * Sorting of the rendered categories - the direction, the value to sort
	 * by (an aggregation over the series or a picked series) and the top N
	 * limit. Only the chart is sorted: the report grid keeps the data order,
	 * the view options reference its cells (=A1).
	 */
	renderSortingForm = () => {
		const sorting = this.viewOptions.sorting ?? {};
		const names = this.chart?.seriesNames ?? [];
		return html`
			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					@change="${(e: events.ChangeEvent) => this.updateProperty('sorting.order', e.value === '' ? undefined : e.value)}"
					.caption="${msg('Order')}"
					.items="${[
						{k: '', v: msg('Auto')},
						{k: 'asc', v: msg('Ascending')},
						{k: 'desc', v: msg('Descending')},
					]}"
					.value="${sorting.order ?? ''}">
				</og-dropdown-list>

				<og-dropdown-list
					class="field"
					color="transparent"
					@change="${(e: events.ChangeEvent) => this.updateProperty('sorting.by', /^\d+$/.test(e.value as string) ? Number(e.value) : e.value)}"
					.caption="${msg('By value')}"
					.items="${[
						{k: 'sum', v: msg('Sum')},
						{k: 'min', v: msg('Minimum')},
						{k: 'max', v: msg('Maximum')},
						{k: 'avg', v: msg('Average')},
						...names.map((name, index) => ({k: index.toString(), v: name || `${msg('Series')} ${index + 1}`})),
					]}"
					.value="${(sorting.by ?? 'sum').toString()}">
				</og-dropdown-list>

				<og-input
					class="field"
					data-key="sorting.limit"
					data-type="number"
					@change="${this._onDataChange}"
					.placeholder="${msg('Top X')}"
					.value="${sorting.limit ?? ''}">
				</og-input>
			</div>
		`;
	};

	/**
	 * Container of the sorting form. The data form is rendered statically
	 * into the split container, so the form re-renders through this cached
	 * element - the series names arrive once the chart loads (see updated).
	 */
	private _sortingForm: HTMLDivElement;

	private get sortingForm() {
		if (!this._sortingForm) {
			this._sortingForm = document.createElement('div');
			render(this.renderSortingForm(), this._sortingForm);
		}
		return this._sortingForm;
	}

	/**
	 * The plot band bounds accept report cell references, so they belong to
	 * the data tab next to the report data grid (see the gauge editor).
	 */
	renderDataForm = () => html`
		<style>${BaseVisualizationEditor.styles}</style>

		<og-panel
			collapsible
			?collapsed="${false}"
			style="border-top: none">

			<div class="header" slot="header">
				<og-icon icon="sort" fixed-width></og-icon> ${msg('Sorting')}
			</div>

			<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.sortingForm}</div>
		</og-panel>

		<og-panel
			collapsible
			?collapsed="${false}">

			<div class="header" slot="header">
				<og-icon icon="palette" fixed-width></og-icon> ${msg('Y axis plot bands')}
			</div>

			<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderPlotBandsForm('y_axis.plot_bands')}</div>
		</og-panel>

		<og-panel
			collapsible
			?collapsed="${false}">

			<div class="header" slot="header">
				<og-icon icon="palette" fixed-width></og-icon> ${msg('X axis plot bands')}
			</div>

			<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderPlotBandsForm('x_axis.plot_bands')}</div>
		</og-panel>
	`;

	renderPropsSection = () => html`
		<og-container style="height: 100%">
			<div slot="content">
				<og-panel
					collapsible
					?collapsed="${false}">

					<div class="header" slot="header">
						<og-icon icon="gear" fixed-width></og-icon> ${msg('Chart')}
					</div>

					<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderChartForm()}</div>
				</og-panel>

				<og-panel
					collapsible
					?collapsed="${false}">

					<div class="header" slot="header">
						<og-icon icon="list" fixed-width></og-icon> ${msg('Legend')}
					</div>

					<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderLegendForm()}</div>
				</og-panel>

				<og-panel
					collapsible
					?collapsed="${false}">

					<div class="header" slot="header">
						<og-icon icon="chart-line" fixed-width></og-icon> ${msg('Y axis')}
					</div>

					<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderAxisForm('y_axis')}</div>
				</og-panel>

				<og-panel
					collapsible
					?collapsed="${false}">

					<div class="header" slot="header">
						<og-icon icon="chart-bar" fixed-width></og-icon> ${msg('X axis')}
					</div>

					<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderAxisForm('x_axis')}</div>
				</og-panel>
			</div>
		</og-container>
	`;

	renderSeriesSection = () => html`
		<div slot="content" style="height: 100%">
			<og-grid ${ref(el => this.setSeriesGrid(el as Grid))}
				style="height: 100%"
				@grid.edit="${this._onSeriesEdit}">
			</og-grid>
		</div>
	`;

	updated(props: Map<string, unknown>) {
		super.updated(props);
		if (props.has('reportContext') || props.has('result')) {
			this.loadDataGrid();
		}
		// follow the loaded series names and the context switches
		if (this._sortingForm) render(this.renderSortingForm(), this._sortingForm);
		// monaco needs a relayout after the tab reattached it
		if (props.has('activeTab') && this.activeTab == 'json') {
			this.highchartOptionsEditor.layout();
		}
	}

	render = () => html`
		<div class="head">
			<og-bucket-button
				.text="${msg('Data').toUpperCase()}"
				icon="table"
				@click="${() => this.activeTab = 'data'}"
				?active="${this.activeTab == 'data'}">
			</og-bucket-button>

			<og-bucket-button
				.text="${msg('Chart properties').toUpperCase()}"
				icon="gear"
				@click="${() => this.activeTab = 'props'}"
				?active="${this.activeTab == 'props'}">
			</og-bucket-button>

			<og-bucket-button
				.text="${msg('Series').toUpperCase()}"
				icon="chart-line"
				@click="${() => this.activeTab = 'series'}"
				?active="${this.activeTab == 'series'}">
			</og-bucket-button>

			<og-bucket-button
				.text="${msg('Highcharts').toUpperCase()}"
				icon="code"
				@click="${() => this.activeTab = 'json'}"
				?active="${this.activeTab == 'json'}">
			</og-bucket-button>
		</div>

		<div class="body">
			${choose(this.activeTab, [
				['data', () => this.renderDataSection()],
				['props', () => this.renderPropsSection()],
				['series', () => this.renderSeriesSection()],
				['json', () => this.renderHighchartOptionsSection()],
			])}
		</div>
	`;

}
